import { View, SideNavView, ContextMenu, TableView, DataView, Dialog } from 'web-mojo';
import { Operator } from '@portal/models/Operator.js';
import { RouteList } from '@portal/models/Route.js';

class OperatorView extends View {
    constructor(options = {}) {
        super({
            className: 'operator-view',
            viewDialogOptions: {
                size: 'md'
            },
            ...options
        });

        this.template = `
            <div class="operator-view-container">
                <div data-container="header"></div>
                <div data-container="sidenav" style="min-height: 400px;"></div>
            </div>
        `;
    }

    async onInit() {
        const status = this.model.get('status') || 'inactive';
        const isActive = status === 'active';

        // Header
        this.statusBadgeHTML = isActive
            ? '<span class="badge bg-success">Active</span>'
            : status === 'on_route'
                ? '<span class="badge bg-primary">On Route</span>'
                : '<span class="badge bg-secondary">Inactive</span>';

        this.header = new View({
            containerId: 'header',
            template: `
            <div class="d-flex justify-content-between align-items-start mb-4">
                <div class="d-flex align-items-center gap-3">
                    <div class="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                         style="width: 56px; height: 56px;">
                        <i class="bi bi-person-badge text-primary" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                        <h3 class="mb-0">{{model.display_name|default(model.user.username)|default('Operator')}}</h3>
                        <div class="text-muted small mt-1">{{model.user.email}}</div>
                    </div>
                </div>
                <div class="d-flex align-items-start gap-3">
                    <div class="text-end">
                        {{{statusBadge}}}
                    </div>
                    <div data-container="context-menu"></div>
                </div>
            </div>
            `
        });
        this.header.statusBadge = this.statusBadgeHTML;
        this.header.setModel(this.model);
        this.addChild(this.header);

        // Context menu
        const menuItems = [
            { label: 'Edit', action: 'edit', icon: 'bi-pencil' },
        ];
        if (isActive) {
            menuItems.push({ type: 'divider' });
            menuItems.push({ label: 'Deactivate', action: 'deactivate', icon: 'bi-person-dash' });
        }
        const contextMenu = new ContextMenu({
            containerId: 'context-menu',
            className: 'context-menu-view header-menu-absolute',
            context: this.model,
            config: {
                icon: 'bi-three-dots-vertical',
                items: menuItems
            }
        });
        this.addChild(contextMenu);

        // Details section
        const detailsView = new DataView({
            model: this.model,
            className: 'p-3',
            showEmptyValues: true,
            fields: [
                { name: 'user.username', label: 'Username', colSize: 6 },
                { name: 'user.email', label: 'Email', colSize: 6 },
                { name: 'phone', label: 'Phone', colSize: 6 },
                { name: 'status', label: 'Status', colSize: 6 },
                { name: 'created_at|datetime', label: 'Added', colSize: 6 },
            ]
        });

        // Recent Routes section
        const routesView = new TableView({
            collection: new RouteList({
                params: { operator: this.model.get('id'), size: 10, sort: '-scheduled_date' }
            }),
            fetchOnMount: true,
            hideActivePillNames: ['operator'],
            clickAction: 'view',
            columns: [
                { key: 'scheduled_date', label: 'Date', formatter: 'date' },
                { key: 'template.name', label: 'Route', formatter: "default('\u2014')" },
                { key: 'status|badge:scheduled=secondary,in_progress=primary,completed=success,cancelled=dark', label: 'Status' },
            ]
        });

        // SideNavView
        this.sideNavView = new SideNavView({
            containerId: 'sidenav',
            activeSection: 'details',
            navWidth: 180,
            contentPadding: '1.25rem 2rem',
            enableResponsive: true,
            minWidth: 500,
            sections: [
                { key: 'details', label: 'Details', icon: 'bi-info-circle', view: detailsView },
                { key: 'routes', label: 'Routes', icon: 'bi-signpost-split', view: routesView },
            ]
        });
        this.addChild(this.sideNavView);
    }

    async onActionDeactivate() {
        const confirmed = await Dialog.confirm({
            title: 'Deactivate Operator',
            message: `Deactivate "${this.model.nameOrId()}"? They will be removed from future route assignments.`,
            confirmText: 'Deactivate',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await this.model.save({ deactivate: 1 });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to deactivate operator');
            return;
        }
        const cascade = response.data.data?.cascade;
        app.toast.success(cascade
            ? `${cascade.access_windows_revoked ?? 0} windows revoked, ${cascade.scheduled_routes_flagged ?? 0} routes flagged`
            : 'Operator deactivated');
        this.render();
        this.emit('changed');
    }

    async onActionEdit() {
        const form = new OperatorEditForm({ operator: this.model });
        Dialog.showDialog({
            title: 'Edit Operator',
            body: form,
            size: 'sm',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Save', class: 'btn-primary',
                  handler: async () => {
                      const ok = await form.onActionSave();
                      if (ok) {
                          this.render();
                          this.emit('changed');
                      }
                      return ok;
                  }
                },
            ],
        });
    }

    _onModelChange() {}
}

// -- Inline edit form ---------------------------------------------------------

class OperatorEditForm extends View {
    constructor({ operator }) {
        super();
        this.operator = operator;
    }

    async onActionSave() {
        const phone = this.element.querySelector('[name=phone]').value.trim();
        const status = this.element.querySelector('[name=status]').value;

        const response = await this.operator.save({
            phone: phone || null,
            status,
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to save operator');
            return false;
        }
        app.toast.success('Operator saved');
        return true;
    }

    set template(v) { this._t = v; }

    get template() {
        const op = this.operator;
        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Phone</label>
                    <input type="text" class="form-control" name="phone" value="${op.get('phone') || ''}">
                </div>
                <div class="mb-4">
                    <label class="form-label">Status</label>
                    <select class="form-select" name="status">
                        <option value="active" ${op.get('status') === 'active' ? 'selected' : ''}>Active</option>
                        <option value="inactive" ${op.get('status') === 'inactive' ? 'selected' : ''}>Inactive</option>
                    </select>
                </div>
            </div>
        `;
    }
}

Operator.VIEW_CLASS = OperatorView;

export default OperatorView;
